import React, { useEffect, useRef, useState, useCallback } from "react";
import FinancialService from "../../rest/financialService";
import PaymentService from "../../rest/paymentsService";
import AssetsService from "../../rest/assetsService";
import CustomerService from "../../rest/customerService";
import ClientService from "../../rest/clientService";
import { formatCurrency } from "../../utils/CurrencyUtils";
import {
    MdAttachMoney, MdPerson, MdQrCode, MdCloudUpload, MdCheckCircle,
    MdSentimentVerySatisfied, MdWallet, MdAdd, MdCreditCard, MdClose,
    MdContentCopy, MdCheckCircleOutline, MdHistory
} from "react-icons/md";
import { AiOutlineLoading } from "react-icons/ai";
import TopNavBar from "../../components/reusables/topNavBar";
import AsaasSeal from "../../components/reusables/asaasSeal";
import WebSocketManager from "../../ws/client";

const STATUS_LABELS = {
    PENDING:   { label: "Pendente",    className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    CONFIRMED: { label: "Confirmado",  className: "bg-green-50 text-green-700 border-green-200" },
    FAILED:    { label: "Falhou",      className: "bg-red-50 text-red-700 border-red-200" },
};

function FinancialCustomerPage() {
    const [outstandingPayments, setOutstandingPayments] = useState([]);
    const [rechargeHistory, setRechargeHistory]         = useState([]);
    const [asaasEnabled, setAsaasEnabled]               = useState(false);
    const [isLoading, setIsLoading]                     = useState(true);

    // recharge modal state
    const [rechargeModal, setRechargeModal]   = useState(false);
    const [rechargeTab, setRechargeTab]       = useState("PIX");
    const [rechargeAmount, setRechargeAmount] = useState("");
    const [selectedStudent, setSelectedStudent] = useState(null); // full StudentDTO
    const [students, setStudents]             = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [cardData, setCardData]             = useState({ number: "", expiryMonth: "", expiryYear: "", cvv: "", holderName: "", holderCpfCnpj: "", holderEmail: "", holderPhone: "", holderPostalCode: "", holderAddressNumber: "", holderProvince: "" });
    const [rechargeLoading, setRechargeLoading] = useState(false);
    const [rechargeError, setRechargeError] = useState("");
    const [minRechargeValue, setMinRechargeValue] = useState(0);

    // PIX QR state
    const [pixRecharge, setPixRecharge] = useState(null); // { qrCodeEncodedImage, qrCodePayload }
    const [copied, setCopied] = useState(false);
    const [loadingQr, setLoadingQr] = useState(null); // rechargeId being fetched

    // debt upload
    const fileInputRef = useRef(null);
    const [selectedPaymentForUpload, setSelectedPaymentForUpload] = useState(null);
    const [visibleRecharges, setVisibleRecharges] = useState(10);
    const RECHARGE_PAGE = 10;

    const refreshRechargeHistory = useCallback(async () => {
        try {
            const history = await FinancialService.getRechargeHistory().catch(() => []);
            setRechargeHistory(Array.isArray(history) ? history : []);
        } catch (error) {
            console.error("Erro ao atualizar histórico de recargas:", error);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                const clientCode = sessionStorage.getItem("clientCode");
                const [payments, integrationStatus, prefs] = await Promise.all([
                    FinancialService.getOustandingPayments(),
                    PaymentService.getIntegrationStatus(clientCode).catch(() => false),
                    ClientService.getClientAdminPreferences().catch(() => null),
                ]);
                setMinRechargeValue(prefs?.min_credit_recharge_value || 0);
                setOutstandingPayments(Array.isArray(payments) ? payments : []);
                setAsaasEnabled(!!integrationStatus);

                if (integrationStatus) {
                    await refreshRechargeHistory();
                }
            } catch (error) {
                console.error("Erro ao inicializar página financeira:", error);
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, [refreshRechargeHistory]);

    useEffect(() => {
        const clientCode = sessionStorage.getItem("clientCode");
        if (!clientCode) return;

        const topic = `/topic/clients/${clientCode}/students`;
        const callback = (event) => {
            if (event.type === 'UPDATED') {
                refreshRechargeHistory();
            }
        };

        WebSocketManager.subscribe(topic, callback);
        return () => WebSocketManager.unsubscribe(topic);
    }, [refreshRechargeHistory]);

    const openRechargeModal = async (prefilledLogin = null, amount = "") => {
        setRechargeTab("PIX");
        setRechargeError("");
        setRechargeAmount(amount ? String(amount) : "");
        setSelectedStudent(null);
        setCardData({ number: "", expiryMonth: "", expiryYear: "", cvv: "", holderName: "", holderCpfCnpj: "", holderEmail: "", holderPhone: "", holderPostalCode: "", holderAddressNumber: "", holderProvince: "" });
        setRechargeModal(true);

        setStudentsLoading(true);
        try {
            const data = await CustomerService.findStudentsByParent(sessionStorage.getItem("login"));
            const list = Array.isArray(data) ? data : [];
            setStudents(list);
            if (prefilledLogin) {
                const match = list.find(s => s.login === prefilledLogin || s.login_student === prefilledLogin);
                if (match) setSelectedStudent(match);
            }
        } catch (e) {
            console.error("Erro ao buscar alunos:", e);
            setStudents([]);
        } finally {
            setStudentsLoading(false);
        }
    };

    const closeRechargeModal = () => {
        setRechargeModal(null);
        setPixRecharge(null);
        setRechargeError("");
    };

    const handleRechargeSubmit = async () => {
        if (!selectedStudent) { setRechargeError("Selecione um aluno."); return; }
        const parsedAmount = parseFloat(rechargeAmount.replace(",", "."));
        if (!parsedAmount || parsedAmount <= 0) { setRechargeError("Informe um valor válido."); return; }
        if (minRechargeValue > 0 && parsedAmount < minRechargeValue) { setRechargeError(`O valor mínimo para recarga é ${formatCurrency(minRechargeValue)}.`); return; }

        setRechargeLoading(true);
        setRechargeError("");

        try {
            const studentLogin = selectedStudent.login || selectedStudent.login_student;
            const payload = {
                studentLogin,
                parentLogin:  sessionStorage.getItem("login"),
                clientCode:   sessionStorage.getItem("clientCode"),
                amount:       parsedAmount,
                method:       rechargeTab === "PIX" ? "PIX" : "CREDIT_CARD",
                creditCardToken: null,
                creditCardData:  rechargeTab === "CARD" ? cardData : null,
            };

            const response = await PaymentService.createCreditRecharge(payload);

            if (rechargeTab === "PIX" && response?.qrCodeEncodedImage) {
                setPixRecharge({ qrCodeEncodedImage: response.qrCodeEncodedImage, qrCodePayload: response.qrCodePayload });
            } else {
                // card recharge — close modal and refresh history
                closeRechargeModal();
                await refreshRechargeHistory();
            }
        } catch (err) {
            console.error("Erro na recarga:", err);
            setRechargeError(err?.response?.data?.message || "Erro ao processar recarga. Tente novamente.");
        } finally {
            setRechargeLoading(false);
        }
    };

    const handlePixRechargeClose = async () => {
        setPixRecharge(null);
        closeRechargeModal();
        await refreshRechargeHistory();
    };

    const handleCopyPix = () => {
        if (!pixRecharge?.qrCodePayload) return;
        navigator.clipboard.writeText(pixRecharge.qrCodePayload);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleViewPixQr = async (tx) => {
        setLoadingQr(tx.asaasRechargeId);
        try {
            const data = await PaymentService.getPixQrCode(tx.asaasRechargeId);
            const expired = data?.qrCodeExpiresAt && new Date(data.qrCodeExpiresAt) <= new Date();
            if (!expired && (data?.qrCodeEncodedImage || data?.qrCodePayload)) {
                setPixRecharge({ qrCodeEncodedImage: data.qrCodeEncodedImage, qrCodePayload: data.qrCodePayload });
            }
        } catch (e) {
            console.error("Erro ao buscar QR code:", e);
        } finally {
            setLoadingQr(null);
        }
    };

    // Debt payment via Asaas PIX — pre-selects the student and amount
    const handleDebtPayment = (payment) => {
        const login = payment.studentName?.split("/")[0]?.trim() || payment.studentName;
        openRechargeModal(login, Math.abs(payment.amount));
    };

    const handleUploadClick = (payment) => {
        setSelectedPaymentForUpload(payment);
        setTimeout(() => fileInputRef.current?.click(), 0);
    };

    const handleFileChange = async (event) => {
        const payment = selectedPaymentForUpload;
        if (!payment) return;
        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("clientId", payment.studentName);
        formData.append("total", String(payment.amount));
        formData.append("clientCode", sessionStorage.getItem("clientCode"));

        try {
            await AssetsService.uploadPaymentReceipt(formData);
            alert("Comprovante enviado com sucesso!");
            window.location.reload();
        } catch (error) {
            console.error("Erro ao enviar comprovante:", error);
            alert("Erro ao enviar comprovante.");
        } finally {
            event.target.value = null;
            setSelectedPaymentForUpload(null);
        }
    };

    return (
        <div className="h-screen flex flex-col bg-slate-50 font-poppins text-custom_gray_font">
            <TopNavBar title="Financeiro" subtitle="Minha Carteira" />

            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept="image/*,application/pdf" />

            <div className="flex-1 overflow-y-auto p-4 md:p-8">
                <div className="max-w-4xl mx-auto space-y-8">

                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                            <AiOutlineLoading className="animate-spin text-5xl mb-4 text-custom_blue" />
                            <p>Carregando...</p>
                        </div>
                    ) : (
                        <>

                        {/* ── Pagamentos Pendentes ── */}
                                                    <section>
                                                        <div className="flex items-center gap-2 mb-4">
                                                            <MdAttachMoney className="text-red-500 text-2xl" />
                                                            <h2 className="text-lg font-bold text-gray-800">Pagamentos Pendentes</h2>
                                                        </div>

                                                        {outstandingPayments.length > 0 ? (
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                                {outstandingPayments.map((payment, index) => (
                                                                    <div key={index} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col justify-between hover:shadow-md transition-shadow duration-300">
                                                                        <div>
                                                                            <div className="flex justify-between items-start mb-3">
                                                                                <div className="bg-red-50 text-red-600 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide border border-red-100">
                                                                                    {payment.type === "balance" ? "Saldo Devedor" : "Pendência"}
                                                                                </div>
                                                                                <MdAttachMoney className="text-gray-300 text-xl" />
                                                                            </div>
                                                                            <div className="mb-4">
                                                                                <div className="flex items-center gap-2 mb-1">
                                                                                    <MdPerson className="text-custom_blue" size={18} />
                                                                                    <span className="text-xs text-gray-500 font-semibold uppercase">Aluno</span>
                                                                                </div>
                                                                                <p className="text-base font-bold text-gray-800 ml-6 truncate">
                                                                                    {payment.studentName.split("/")[1]?.trim() || payment.studentName}
                                                                                </p>
                                                                            </div>
                                                                            <div className="mb-4">
                                                                                <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Valor Pendente</p>
                                                                                <p className="text-2xl font-bold text-red-500">{formatCurrency(payment.amount)}</p>
                                                                            </div>
                                                                        </div>

                                                                        <div className="space-y-2 mt-auto border-t border-slate-100 pt-3">
                                                                            {payment.details?.includes("PaymentReceiptUploaded") ? (
                                                                                <div className="bg-green-50 border border-green-200 rounded-lg p-2 flex items-center gap-2 text-green-700">
                                                                                    <MdCheckCircle className="text-lg shrink-0" />
                                                                                    <p className="text-xs font-medium leading-tight">
                                                                                        Comprovante enviado!<br />
                                                                                        <span className="text-[10px] font-normal opacity-80">Aguardando conciliação.</span>
                                                                                    </p>
                                                                                </div>
                                                                            ) : (
                                                                                <>
                                                                                    {asaasEnabled ? (
                                                                                        <button
                                                                                            onClick={() => handleDebtPayment(payment)}
                                                                                            className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-custom_blue text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors shadow-sm text-sm"
                                                                                        >
                                                                                            <MdQrCode size={18} />
                                                                                            <span>Pagar com PIX</span>
                                                                                        </button>
                                                                                    ) : null}
                                                                                    <button
                                                                                        onClick={() => handleUploadClick(payment)}
                                                                                        className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors shadow-sm text-sm"
                                                                                    >
                                                                                        <MdCloudUpload size={18} />
                                                                                        <span>Anexar Comprovante</span>
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col items-center justify-center p-10 bg-white rounded-3xl border-2 border-dashed border-slate-200 text-center">
                                                                <div className="bg-green-100 p-5 rounded-full mb-4">
                                                                    <MdSentimentVerySatisfied className="text-green-500 text-5xl" />
                                                                </div>
                                                                <h3 className="text-xl font-bold text-gray-800 mb-1">Tudo em dia!</h3>
                                                                <p className="text-gray-500 text-sm max-w-xs">Não há pendências financeiras registradas para os alunos vinculados.</p>
                                                            </div>
                                                        )}
                                                    </section>

                            {/* ── Carteira Digital (Asaas only) ── */}
                            {asaasEnabled && (
                                <section>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <MdWallet className="text-custom_blue text-2xl" />
                                            <h2 className="text-lg font-bold text-gray-800">Carteira Digital</h2>
                                        </div>
                                        <button
                                            onClick={() => openRechargeModal()}
                                            className="flex items-center gap-2 bg-custom_blue text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors text-sm shadow-sm"
                                        >
                                            <MdAdd size={18} />
                                            Recarregar Saldo
                                        </button>
                                    </div>

                                    {rechargeHistory.length > 0 ? (
                                        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
                                            {rechargeHistory.slice(0, visibleRecharges).map((tx, i) => {
                                                const statusInfo = STATUS_LABELS[tx.asaasStatus] || STATUS_LABELS.PENDING;
                                                const studentName = tx.customer_id?.split("/")[1]?.trim() || tx.customer_id;
                                                const txDate = new Date(tx.transactionDate).toLocaleDateString("pt-BR", { timeZone: 'America/Sao_Paulo' });
                                                return (
                                                    <div key={i} className="flex flex-col min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between gap-2 px-4 py-3">
                                                        {/* Lado esquerdo — aluno + método/data */}
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <MdHistory className="text-gray-400 text-xl shrink-0" />
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-semibold text-gray-800 truncate">{studentName}</p>
                                                                <p className="text-xs text-gray-400 truncate">{tx.paymentMethod} · {txDate}</p>
                                                            </div>
                                                        </div>
                                                        {/* Lado direito — valor + status + ação */}
                                                        <div className="flex items-center gap-2 flex-wrap pl-8 min-[480px]:pl-0 shrink-0">
                                                            <span className="text-sm font-bold text-gray-800 whitespace-nowrap">{formatCurrency(tx.value)}</span>
                                                            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded border whitespace-nowrap ${statusInfo.className}`}>
                                                                {statusInfo.label}
                                                            </span>
                                                            {tx.asaasStatus === 'PENDING' && tx.paymentMethod === 'PIX' && (
                                                                <button
                                                                    onClick={() => handleViewPixQr(tx)}
                                                                    disabled={loadingQr === tx.asaasRechargeId}
                                                                    className="flex items-center gap-1 text-xs font-semibold text-custom_blue hover:text-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                                                                    title="Ver QR Code PIX"
                                                                >
                                                                    {loadingQr === tx.asaasRechargeId
                                                                        ? <AiOutlineLoading className="animate-spin" size={16} />
                                                                        : <MdQrCode size={16} />}
                                                                    Ver QR
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Carregar mais */}
                                            {visibleRecharges < rechargeHistory.length && (
                                                <div className="px-5 py-3 text-center border-t border-slate-100">
                                                    <button
                                                        onClick={() => setVisibleRecharges(v => v + RECHARGE_PAGE)}
                                                        className="text-sm font-semibold text-custom_blue hover:underline"
                                                    >
                                                        Carregar mais ({rechargeHistory.length - visibleRecharges} restantes)
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-gray-400 text-sm">
                                            Nenhuma recarga realizada ainda.
                                        </div>
                                    )}
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Recharge Modal ── */}
            {rechargeModal && !pixRecharge && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between p-5 border-b border-slate-100">
                            <h3 className="text-lg font-bold text-gray-800">Recarregar Saldo</h3>
                            <button onClick={closeRechargeModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <MdClose size={22} />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Student selector */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Aluno</label>
                                {studentsLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                                        <AiOutlineLoading className="animate-spin" /> Carregando alunos...
                                    </div>
                                ) : students.length === 0 ? (
                                    <p className="text-sm text-gray-400">Nenhum aluno vinculado encontrado.</p>
                                ) : (
                                    <div className="grid grid-cols-2 gap-2">
                                        {students.map(s => {
                                            const login = s.login || s.login_student;
                                            const name  = s.name?.split("/")[1]?.trim() || s.name || login;
                                            const credit = s.credit ?? 0;
                                            const isSelected = selectedStudent && (selectedStudent.login || selectedStudent.login_student) === login;
                                            return (
                                                <button
                                                    key={login}
                                                    type="button"
                                                    onClick={() => setSelectedStudent(s)}
                                                    className={`flex flex-col items-start px-3 py-2.5 rounded-xl border-2 text-left transition-all ${
                                                        isSelected
                                                            ? "border-custom_blue bg-blue-50"
                                                            : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
                                                    }`}
                                                >
                                                    <span className="text-sm font-bold text-gray-800 truncate w-full">{name}</span>
                                                    <span className={`text-xs font-semibold mt-0.5 ${credit < 0 ? "text-red-500" : "text-green-600"}`}>
                                                        {formatCurrency(credit)}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Valor (R$)</label>
                                <input
                                    type="number"
                                    min={minRechargeValue > 0 ? minRechargeValue : "0.01"}
                                    step="0.01"
                                    value={rechargeAmount}
                                    onChange={e => setRechargeAmount(e.target.value)}
                                    placeholder="0,00"
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-custom_blue"
                                />
                                {minRechargeValue > 0 && (
                                    <p className="text-xs text-gray-400 mt-1">Valor mínimo: {formatCurrency(minRechargeValue)}</p>
                                )}
                            </div>

                            {/* Payment method tabs */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Forma de Pagamento</label>
                                <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                                    <button
                                        onClick={() => setRechargeTab("PIX")}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-semibold transition-colors ${rechargeTab === "PIX" ? "bg-custom_blue text-white" : "bg-white text-gray-600 hover:bg-slate-50"}`}
                                    >
                                        <MdQrCode size={16} /> PIX
                                    </button>
                                    <button
                                        onClick={() => setRechargeTab("CARD")}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-semibold transition-colors ${rechargeTab === "CARD" ? "bg-custom_blue text-white" : "bg-white text-gray-600 hover:bg-slate-50"}`}
                                    >
                                        <MdCreditCard size={16} /> Cartão de Crédito
                                    </button>
                                </div>
                            </div>

                            {/* Credit card form */}
                            {rechargeTab === "CARD" && (
                                <div className="space-y-3 border border-slate-100 rounded-xl p-4 bg-slate-50">
                                    {[
                                        { label: "Número do Cartão", key: "number", placeholder: "0000 0000 0000 0000" },
                                        { label: "Nome no Cartão", key: "holderName", placeholder: "Como impresso no cartão" },
                                        { label: "CPF/CNPJ do Titular", key: "holderCpfCnpj", placeholder: "000.000.000-00" },
                                        { label: "E-mail do Titular", key: "holderEmail", placeholder: "email@exemplo.com" },
                                    ].map(({ label, key, placeholder }) => (
                                        <div key={key}>
                                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{label}</label>
                                            <input
                                                type="text"
                                                placeholder={placeholder}
                                                value={cardData[key]}
                                                onChange={e => setCardData(prev => ({ ...prev, [key]: e.target.value }))}
                                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-custom_blue"
                                            />
                                        </div>
                                    ))}
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { label: "Mês", key: "expiryMonth", placeholder: "MM" },
                                            { label: "Ano", key: "expiryYear", placeholder: "AAAA" },
                                            { label: "CVV", key: "cvv", placeholder: "000" },
                                        ].map(({ label, key, placeholder }) => (
                                            <div key={key}>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{label}</label>
                                                <input
                                                    type="text"
                                                    placeholder={placeholder}
                                                    value={cardData[key]}
                                                    onChange={e => setCardData(prev => ({ ...prev, [key]: e.target.value }))}
                                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-custom_blue"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { label: "CEP", key: "holderPostalCode", placeholder: "00000-000" },
                                            { label: "Número", key: "holderAddressNumber", placeholder: "123" },
                                            { label: "Bairro", key: "holderProvince", placeholder: "Bairro" },
                                        ].map(({ label, key, placeholder }) => (
                                            <div key={key}>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{label}</label>
                                                <input
                                                    type="text"
                                                    placeholder={placeholder}
                                                    value={cardData[key]}
                                                    onChange={e => setCardData(prev => ({ ...prev, [key]: e.target.value }))}
                                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-custom_blue"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {rechargeError && (
                                <p className="text-sm text-red-500 font-medium">{rechargeError}</p>
                            )}
                        </div>

                        <div className="px-5 pb-5">
                            <button
                                onClick={handleRechargeSubmit}
                                disabled={rechargeLoading}
                                className="w-full flex items-center justify-center gap-2 bg-custom_blue text-white py-3 rounded-xl font-bold hover:bg-blue-600 transition-colors disabled:opacity-60"
                            >
                                {rechargeLoading
                                    ? <><AiOutlineLoading className="animate-spin" /> Processando...</>
                                    : rechargeTab === "PIX" ? <><MdQrCode size={18} /> Gerar QR Code PIX</> : <><MdCreditCard size={18} /> Pagar com Cartão</>
                                }
                            </button>

                            {asaasEnabled && (
                                <div className="flex justify-center mt-4">
                                    <AsaasSeal size="sm" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── PIX QR Modal ── */}
            {pixRecharge && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm text-center">
                        <div className="p-5 border-b border-slate-100">
                            <h3 className="text-lg font-bold text-gray-800">Escaneie o QR Code PIX</h3>
                            <p className="text-sm text-gray-500 mt-1">Aguardando confirmação do pagamento</p>
                        </div>

                        <div className="p-5 space-y-4">
                            {pixRecharge.qrCodeEncodedImage && (
                                <img
                                    src={`data:image/png;base64,${pixRecharge.qrCodeEncodedImage}`}
                                    alt="QR Code PIX"
                                    className="w-56 h-56 mx-auto border border-slate-200 rounded-lg"
                                />
                            )}

                            {pixRecharge.qrCodePayload && (
                                <div className="space-y-2">
                                    <p className="text-xs text-gray-500">Ou copie o código Pix Copia e Cola:</p>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                        <span className="flex-1 text-xs text-gray-700 truncate">{pixRecharge.qrCodePayload}</span>
                                        <button onClick={handleCopyPix} className="shrink-0 text-custom_blue hover:text-blue-700 transition-colors">
                                            {copied ? <MdCheckCircleOutline size={20} className="text-green-500" /> : <MdContentCopy size={20} />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                                <p className="text-xs text-blue-700">O saldo será adicionado automaticamente após a confirmação do pagamento.</p>
                            </div>
                        </div>

                        <div className="px-5 pb-5">
                            <button
                                onClick={handlePixRechargeClose}
                                className="w-full bg-custom_blue text-white py-3 rounded-xl font-bold hover:bg-blue-600 transition-colors"
                            >
                                OK, já realizei o pagamento
                            </button>

                            {asaasEnabled && (
                                <div className="flex justify-center mt-4">
                                    <AsaasSeal size="sm" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FinancialCustomerPage;
